import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { IconChevronDown, IconSearch } from '@tabler/icons-react';
import { AnimatedLabel } from '../ui/AnimatedLabel';
import { AnimatedPresence } from '../ui/AnimatedPresence';
import { CheckIcon } from '../icons/CheckIcon';
import { CloseIcon } from '../icons/CloseIcon';

/** Controlled choice menu contract, shared by active answers and corrections. */
export interface ChoiceDropdownProps {
  options: readonly { id: string; label: string }[];
  selected: string[];
  multiple: boolean;
  maxSelected?: number;
  disabled?: boolean;
  labelledBy: string;
  describedBy?: string;
  onChange: (ids: string[]) => void;
}

/** Search existing option labels while keeping the answer represented by stable option IDs. */
export function ChoiceDropdown({ options, selected, multiple, maxSelected, disabled = false, labelledBy, describedBy, onChange }: ChoiceDropdownProps) {
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const outsidePointer = useRef(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const visible = options.filter(option => option.label.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
  const chosen = options.filter(option => selected.includes(option.id));
  const expanded = open && !disabled;
  const active = visible[activeIndex];
  const summary = chosen.length ? (multiple ? `${chosen.length} selected` : chosen[0].label) : 'Select options';

  useEffect(() => {
    if (!expanded) return undefined;
    search.current?.focus({ preventScroll: true });
    /** Defer pointer-driven blur until click so collapsing cannot move the pending target. */
    const trackPointer = (event: PointerEvent) => {
      outsidePointer.current = event.target instanceof Node && !root.current?.contains(event.target);
    };
    /** The click target is now fixed, so dismissal preserves the external button's activation. */
    const dismiss = (event: MouseEvent) => {
      outsidePointer.current = false;
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    /** Resume normal focus dismissal after a cancelled gesture or a return to keyboard use. */
    const resumeFocusDismissal = () => {
      outsidePointer.current = false;
      if (!root.current?.contains(document.activeElement)) setOpen(false);
    };
    document.addEventListener('pointerdown', trackPointer, true);
    document.addEventListener('click', dismiss, true);
    document.addEventListener('pointercancel', resumeFocusDismissal, true);
    document.addEventListener('keydown', resumeFocusDismissal, true);
    return () => {
      outsidePointer.current = false;
      document.removeEventListener('pointerdown', trackPointer, true);
      document.removeEventListener('click', dismiss, true);
      document.removeEventListener('pointercancel', resumeFocusDismissal, true);
      document.removeEventListener('keydown', resumeFocusDismissal, true);
    };
  }, [expanded]);

  useEffect(() => {
    const container = list.current;
    const option = container?.children.item(activeIndex);
    if (!(option instanceof HTMLElement) || !container) return;
    // Scroll only the menu so keyboard navigation cannot move another chapter.
    const bottom = option.offsetTop + option.offsetHeight;
    if (option.offsetTop < container.scrollTop) container.scrollTop = option.offsetTop;
    else if (bottom > container.scrollTop + container.clientHeight) container.scrollTop = bottom - container.clientHeight;
  }, [activeIndex, query, expanded]);

  /** Return focus after selection or Escape without scrolling the surrounding chapter. */
  const close = () => {
    setOpen(false);
    trigger.current?.focus({ preventScroll: true });
  };

  /** Open with a fresh search and highlight the current single choice where possible. */
  const show = (last = false) => {
    if (disabled) return;
    setQuery('');
    const current = options.findIndex(option => option.id === selected[0]);
    setActiveIndex(last ? Math.max(0, options.length - 1) : Math.max(0, current));
    setOpen(true);
  };

  /** Preserve deselection at the limit; the caller retains submission and correction policy. */
  const select = (optionId: string) => {
    if (disabled) return;
    const included = selected.includes(optionId);
    if (multiple && !included && maxSelected !== undefined && selected.length >= maxSelected) return;
    onChange(multiple ? (included ? selected.filter(value => value !== optionId) : [...selected, optionId]) : [optionId]);
    if (!multiple) close();
  };

  /** Keep DOM focus in search while exposing the highlighted option to assistive technology. */
  const navigate = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing || event.keyCode === 229) {
      // Corrections render inside a form; composition Enter must never submit it.
      if (event.key === 'Enter') event.preventDefault();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex(index => Math.max(0, Math.min(visible.length - 1, index + direction)));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (active) select(active.id);
    }
  };

  return <div ref={root} className="min-w-0 w-full" onBlur={event => {
    if (!outsidePointer.current && !event.currentTarget.contains(event.relatedTarget)) setOpen(false);
  }} onKeyDown={event => {
    if (expanded && event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
    }
  }}>
    <button ref={trigger} type="button" aria-labelledby={labelledBy} aria-describedby={`${id}-summary${describedBy ? ` ${describedBy}` : ''}`}
      aria-haspopup="listbox" aria-expanded={expanded} aria-controls={expanded ? `${id}-list` : undefined} disabled={disabled}
      onClick={() => expanded ? close() : show()} onKeyDown={event => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); show(event.key === 'ArrowUp'); }
      }}
      className="flex min-h-12 w-full items-center gap-3 rounded-xl border border-border bg-surface-raised px-4 py-3 text-left text-sm text-foreground transition-colors duration-200 ease-[var(--ease-out)] hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none">
      <span id={`${id}-summary`} aria-live="polite" className="min-w-0 flex-1 break-words"><AnimatedLabel text={summary} multiline /></span>
      <IconChevronDown aria-hidden="true" size={18} className={`shrink-0 transition-transform duration-200 ease-[var(--ease-out)] motion-reduce:transition-none ${expanded ? 'rotate-180' : ''}`} />
    </button>
    <AnimatedPresence show={expanded}><div className="pt-2"><div className="min-w-0 rounded-xl border border-border bg-surface-raised p-2">
      <div className="flex min-h-12 items-center gap-2 px-2">
        <IconSearch aria-hidden="true" size={18} className="shrink-0 text-muted-foreground" />
        <input ref={search} role="combobox" aria-label="Search options" aria-autocomplete="list" aria-expanded="true"
          aria-controls={`${id}-list`} aria-activedescendant={active ? `${id}-option-${activeIndex}` : undefined}
          aria-describedby={describedBy} value={query} onChange={event => { setQuery(event.target.value); setActiveIndex(0); }}
          onKeyDown={navigate} placeholder="Search" autoComplete="off"
          className="min-w-0 flex-1 bg-transparent py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none" />
        {query && <button type="button" aria-label="Clear search" onClick={() => { setQuery(''); setActiveIndex(0); search.current?.focus({ preventScroll: true }); }}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-ring">
          <CloseIcon width={18} height={18} aria-hidden="true" />
        </button>}
      </div>
      {multiple && chosen.length > 0 && <div className="flex max-h-28 flex-wrap gap-1 overflow-y-auto overscroll-contain px-2 pb-2" aria-label="Selected options">
        {chosen.map(option => <button type="button" key={option.id} aria-label={`Remove ${option.label}`} onClick={() => { select(option.id); search.current?.focus({ preventScroll: true }); }}
          className="flex min-h-11 max-w-full items-center gap-2 rounded-lg bg-surface-hover px-3 py-2 text-left text-sm text-foreground focus-visible:outline-2 focus-visible:outline-ring">
          <span className="min-w-0 break-words">{option.label}</span><CloseIcon width={16} height={16} aria-hidden="true" className="shrink-0" />
        </button>)}
      </div>}
      <div ref={list} id={`${id}-list`} role="listbox" aria-labelledby={labelledBy} aria-multiselectable={multiple || undefined}
        className="relative max-h-[min(16rem,40dvh)] overflow-y-auto overscroll-contain">
        {visible.map((option, index) => {
          const isSelected = selected.includes(option.id);
          const blocked = multiple && !isSelected && maxSelected !== undefined && selected.length >= maxSelected;
          return <div key={option.id} id={`${id}-option-${index}`} role="option" aria-selected={isSelected} aria-disabled={blocked || undefined}
            onMouseDown={event => event.preventDefault()} onClick={() => select(option.id)} onPointerMove={() => setActiveIndex(index)}
            className={`flex min-h-12 cursor-pointer items-center gap-3 rounded-lg px-3 py-3 text-sm text-foreground ${activeIndex === index ? 'bg-surface-hover' : ''} ${blocked ? 'cursor-not-allowed opacity-50' : ''}`}>
            <span className="min-w-0 flex-1 break-words">{option.label}</span>
            <CheckIcon width={18} height={18} aria-hidden="true" className={`shrink-0 transition-[opacity,transform] duration-200 motion-reduce:transition-none ${isSelected ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`} />
          </div>;
        })}
      </div>
      {visible.length === 0 && <p role="status" className="px-3 py-4 text-sm text-muted-foreground">{options.length ? 'No matches' : 'No options'}</p>}
    </div></div></AnimatedPresence>
  </div>;
}
